import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { Text } from '@clay-run/glaze/text';

/**
 * Plan `2026-09-13-001-playbooks-qa-round4-plan.md`, U1 (R-L3) — lifted
 * verbatim out of `PlaybooksConfigPage.tsx` (where it was module-local,
 * rename-only originally, then reused for the "+ New Segmentation"/"+ New
 * Playbook" create doorways) so `SequencesStep.tsx`'s new in-play Rename
 * drawer (kebab → Rename) can share the exact same field instead of growing
 * a second naming affordance. Props are otherwise unchanged; `warning` is
 * new (Decision 4 / R-D4) — an optional inline caption shown above the
 * actions, e.g. "Used in N places — renaming changes it everywhere."
 */

export interface NameDrawerFieldProps {
  /** Seeded once per mount; the caller `key`s the drawer so a new target remounts. */
  initialName: string;
  saving: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
  /**
   * Decision 4 (R-D4) — rendered as a `Text size="xs" variant="tertiary"`
   * caption above the Cancel/Save row when non-null. `null`/`undefined`
   * renders nothing (the common case: a sequence used in ≤1 place, or a
   * playbook/segmentation rename, which never carries this warning).
   */
  warning?: string | null;
}

/**
 * The shared "what's it called" field. Originally rename-only; the
 * "+ New Segmentation"/"+ New Playbook" entry points reuse the exact same
 * shape (label "Name", Cancel/Save, Save disabled while blank) seeded with an
 * empty string rather than introducing a second naming affordance.
 */
export function NameDrawerField({ initialName, saving, onSave, onCancel, warning }: NameDrawerFieldProps) {
  const [value, setValue] = useState(initialName);
  const trimmed = value.trim();
  return (
    <>
      <InputField
        label="Name"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      {warning && (
        <Text size="xs" variant="tertiary">
          {warning}
        </Text>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={trimmed.length === 0}
          loading={saving}
          onClick={() => onSave(trimmed)}
        >
          Save
        </Button>
      </div>
    </>
  );
}
